/**
 * surface.ts -- THE valuation path. Port of `backtest/surface.py`.
 *
 * Every price this engine produces -- opening fill, daily mark, closing fill,
 * expiry settlement, hedge mark -- comes from `valueOption` or `valueSwap`.
 * Nothing else prices anything.
 *
 * The previous JS port had no term interpolation at all: it re-priced an aged
 * option inside its ORIGINAL bucket with T overridden, which the Python's own
 * docstring called out as wrong (SABR alpha is calibrated *to* each pillar's T).
 * It also priced closes off the nominal pillar T while marking at the remaining
 * life, so every roll booked a spurious jump.
 *
 * Term interpolation: implied vol in total variance, linearly in T
 * (flat-forward-variance); forward and annuity linearly in T. Vols are read at
 * the fixed absolute strike, because the position's strike is fixed. Below the
 * shortest pillar the vol is flat while the forward and annuity extrapolate
 * linearly to T = 0, so the expiry fixing is a genuine spot swap rate produced
 * by the same function that produced yesterday's mark -- which makes value
 * continuous through expiry by construction.
 *
 * Greeks are the CLOSED-FORM Bachelier sensitivities at frozen sigma(K) and
 * frozen annuity (sticky-strike), not finite differences, so gamma does not
 * degrade near expiry. Theta is a one-day difference through the whole
 * interpolator, one-sided when under a day remains.
 */

import {
  type LegValue,
  type MarketSnapshot,
  PricingFailure,
  type SmileBucket,
  tenorSlice,
} from "./core";
import type { PricingEngine } from "./engines";
import { normCdf, normPdf } from "./mathx";

const THETA_BUMP = 1.0 / 365.0;

/** Undiscounted Bachelier price and its analytic (delta, gamma, vega). */
export function bachelier(
  F: number,
  K: number,
  T: number,
  sigma: number,
  omega: number,
): { price: number; delta: number; gamma: number; vega: number } {
  const sqrtT = Math.sqrt(Math.max(T, 0));
  const v = sigma * sqrtT;
  if (v <= 1e-14) {
    const itm = omega * (F - K) > 0;
    return {
      price: Math.max(omega * (F - K), 0),
      delta: itm ? omega : 0,
      gamma: 0,
      vega: 0,
    };
  }
  const d = (F - K) / v;
  return {
    price: omega * (F - K) * normCdf(omega * d) + v * normPdf(d),
    delta: omega * normCdf(omega * d),
    gamma: normPdf(d) / v,
    vega: sqrtT * normPdf(d),
  };
}

// ===========================================================================
// Term slice
// ===========================================================================

export class TermSlice {
  readonly tenor: string;
  readonly buckets: SmileBucket[];
  readonly Ts: number[];
  /**
   * Vol memo, keyed by the engine OBJECT then by (index, strike).
   *
   * Not by any identity-derived number: the Python version of this cache
   * originally keyed on `id(engine)`, and CPython recycles ids after collection,
   * so a short-lived engine's cached vols could be served to a different engine
   * at the same address -- silently making a two-model comparison return
   * identical marks. A Map keyed by the object cannot do that and also keeps the
   * engine alive.
   */
  private readonly volCache = new Map<PricingEngine, Map<string, number>>();

  constructor(buckets: SmileBucket[]) {
    if (buckets.length === 0) throw new PricingFailure("TermSlice needs a pillar");
    this.buckets = [...buckets].sort((a, b) => a.T - b.T);
    this.tenor = this.buckets[0].tenor;
    this.Ts = this.buckets.map((b) => b.T);
  }

  /** (i, j, wi, wj) with T between pillars i and j; extrapolates below Ts[0]. */
  private bracket(T: number): [number, number, number, number] {
    const Ts = this.Ts;
    const n = Ts.length;
    if (n === 1) return [0, 0, 1, 0];
    if (T >= Ts[n - 1]) return [n - 1, n - 1, 1, 0];
    if (T <= Ts[0]) {
      const w1 = (T - Ts[0]) / (Ts[1] - Ts[0]); // negative below Ts[0]
      return [0, 1, 1 - w1, w1];
    }
    for (let i = 0; i < n - 1; i++) {
      if (Ts[i] <= T && T <= Ts[i + 1]) {
        const w2 = (T - Ts[i]) / (Ts[i + 1] - Ts[i]);
        return [i, i + 1, 1 - w2, w2];
      }
    }
    return [n - 1, n - 1, 1, 0];
  }

  forward(T: number): number {
    const [i, j, wi, wj] = this.bracket(T);
    return wi * this.buckets[i].F + wj * this.buckets[j].F;
  }

  annuity(T: number): number {
    const [i, j, wi, wj] = this.bracket(T);
    return Math.max(wi * this.buckets[i].annuity + wj * this.buckets[j].annuity, 1e-12);
  }

  private pillarVol(engine: PricingEngine, idx: number, K: number): number {
    let per = this.volCache.get(engine);
    if (!per) {
      per = new Map();
      this.volCache.set(engine, per);
    }
    const key = `${idx}|${K}`;
    const hit = per.get(key);
    if (hit !== undefined) return hit;
    const v = engine.impliedVol(this.buckets[idx], K);
    if (!Number.isFinite(v) || v <= 0) {
      throw new PricingFailure(
        `engine ${engine.name} returned sigma=${v} at ` +
          `${this.buckets[idx].expiry}x${this.tenor}, K=${K}`,
      );
    }
    per.set(key, v);
    return v;
  }

  /** Flat-forward-variance interpolated vol; flat outside the pillars. */
  vol(engine: PricingEngine, T: number, K: number): number {
    if (T <= 0) return 0;
    const Ts = this.Ts;
    const n = Ts.length;
    if (n === 1 || T <= Ts[0]) return this.pillarVol(engine, 0, K);
    if (T >= Ts[n - 1]) return this.pillarVol(engine, n - 1, K);
    for (let i = 0; i < n - 1; i++) {
      if (Ts[i] <= T && T <= Ts[i + 1]) {
        const T1 = Ts[i];
        const T2 = Ts[i + 1];
        const w2 = (T - T1) / (T2 - T1);
        const w1 = 1 - w2;
        const s1 = this.pillarVol(engine, i, K);
        const s2 = this.pillarVol(engine, i + 1, K);
        const totalVar = w1 * s1 * s1 * T1 + w2 * s2 * s2 * T2;
        return Math.sqrt(Math.max(totalVar, 0) / T);
      }
    }
    return this.pillarVol(engine, n - 1, K);
  }

  bracketReport(T: number) {
    const [i, j, wi, wj] = this.bracket(T);
    const method = i === j ? "flat" : T <= this.Ts[0] ? "extrapolate_short" : "interpolate";
    return {
      lowerPillar: this.buckets[i].expiry,
      lowerT: this.Ts[i],
      upperPillar: this.buckets[j].expiry,
      upperT: this.Ts[j],
      wLower: wi,
      wUpper: wj,
      method,
    };
  }
}

export function getSlice(snap: MarketSnapshot, tenor: string): TermSlice {
  const hit = snap.slices.get(tenor);
  if (hit) return hit as TermSlice;
  const sl = new TermSlice(tenorSlice(snap, tenor));
  snap.slices.set(tenor, sl);
  return sl;
}

// ===========================================================================
// The single valuation entry points
// ===========================================================================

export function valueOption(
  snap: MarketSnapshot,
  engine: PricingEngine,
  tenor: string,
  K: number,
  omega: number,
  Traw: number,
  opts: { volOverride?: number; greeks?: boolean } = {},
): LegValue {
  const greeks = opts.greeks ?? true;
  const sl = getSlice(snap, tenor);
  const T = Math.max(Traw, 0);
  const F = sl.forward(T);
  const ann = sl.annuity(T);

  if (T <= 0) {
    const itm = omega * (F - K) > 0;
    return {
      price: ann * Math.max(omega * (F - K), 0),
      F,
      annuity: ann,
      impliedVol: 0,
      T: 0,
      delta: itm ? ann * omega : 0,
      gamma: 0,
      vega: 0,
      theta: 0,
    };
  }

  const sigma = opts.volOverride ?? sl.vol(engine, T, K);
  const b = bachelier(F, K, T, sigma, omega);
  const lv: LegValue = {
    price: ann * b.price,
    F,
    annuity: ann,
    impliedVol: sigma,
    T,
    delta: ann * b.delta,
    gamma: ann * b.gamma,
    vega: ann * b.vega,
    theta: 0,
  };
  if (!greeks) return lv;

  // Theta: value one day forward with the surface unchanged, i.e. slide down the
  // term axis. Captures Bachelier decay AND the roll-down of the vol, forward
  // and annuity term structures. One-sided when under a day remains, so the step
  // matches the divisor.
  const dt = Math.min(THETA_BUMP, T);
  const Tn = T - dt;
  const Fn = sl.forward(Tn);
  const an = sl.annuity(Tn);
  const pxN =
    Tn <= 0
      ? Math.max(omega * (Fn - K), 0)
      : bachelier(Fn, K, Tn, sl.vol(engine, Tn, K), omega).price;
  lv.theta = dt > 0 ? (an * pxN - lv.price) / dt : 0;
  return lv;
}

export function forwardRate(snap: MarketSnapshot, tenor: string, startT = 0): number {
  return getSlice(snap, tenor).forward(Math.max(startT, 0));
}

export function swapAnnuity(snap: MarketSnapshot, tenor: string, startT = 0): number {
  return getSlice(snap, tenor).annuity(Math.max(startT, 0));
}

/** PV = annuity × (F − rate). Positive notional pays fixed. */
export function valueSwap(
  snap: MarketSnapshot,
  tenor: string,
  rate: number,
  startT = 0,
): LegValue {
  const sl = getSlice(snap, tenor);
  const T = Math.max(startT, 0);
  const F = sl.forward(T);
  const ann = sl.annuity(T);
  return {
    price: ann * (F - rate),
    F,
    annuity: ann,
    impliedVol: 0,
    T,
    delta: ann,
    gamma: 0,
    vega: 0,
    theta: 0,
  };
}

/**
 * Execution price and vol. Buying lifts the offer, selling hits the bid.
 *
 * The mid is always `valueOption`, so an opening fill and the mark an instant
 * later differ by exactly the half-spread -- never by a pricing inconsistency.
 * `bucket` supplies the cost parameters only; the level comes from the
 * interpolated surface at the position's actual remaining life.
 */
export function executionVolAndPrice(
  snap: MarketSnapshot,
  engine: PricingEngine,
  bucket: SmileBucket,
  tenor: string,
  K: number,
  omega: number,
  T: number,
  side: "buy" | "sell",
): { price: number; vol: number; mid: number } {
  const mid = valueOption(snap, engine, tenor, K, omega, T, { greeks: false });
  const sign = side === "buy" ? 1 : -1;

  if (bucket.priceHalfSpread > 0) {
    return {
      price: mid.price + sign * bucket.priceHalfSpread * Math.abs(mid.price),
      vol: mid.impliedVol,
      mid: mid.price,
    };
  }
  const sigmaExec = Math.max(mid.impliedVol + sign * bucket.volHalfSpread, 1e-8);
  const exec = valueOption(snap, engine, tenor, K, omega, T, {
    volOverride: sigmaExec,
    greeks: false,
  });
  return { price: exec.price, vol: sigmaExec, mid: mid.price };
}

/** The pillar closest in remaining life to T. Cost parameters only. */
export function nearestBucket(
  snap: MarketSnapshot,
  tenor: string,
  T: number,
): SmileBucket {
  const list = tenorSlice(snap, tenor);
  let best = list[0];
  let bestD = Math.abs(best.T - T);
  for (const b of list) {
    const d = Math.abs(b.T - T);
    if (d < bestD) {
      best = b;
      bestD = d;
    }
  }
  return best;
}
